import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  createSpecialOffer,
  resetSpecialSuccess,
} from "../../features/reservationSlice";

import { updateUser } from "../../features/userSlice";
import "./Reservation.css";

const formatPhoneDisplay = (value) => {
  const digits = value.replace(/\D/g, "").replace(/^7/, "");
  const clean = digits.slice(0, 10);
  let formatted = "+7";
  if (clean.length > 0) formatted += ` (${clean.slice(0, 3)}`;
  if (clean.length >= 4) formatted += `) ${clean.slice(3, 6)}`;
  if (clean.length >= 7) formatted += `-${clean.slice(6, 8)}`;
  if (clean.length >= 9) formatted += `-${clean.slice(8, 10)}`;
  return formatted;
};

const checkDate = (val) => {
  if (!/^\d{2}\.\d{2}\.\d{4}$/.test(val)) return "Формат ДД.ММ.ГГГГ";
  const [d, m, y] = val.split(".").map(Number);
  const dt = new Date(y, m - 1, d);
  if (dt.getDate() !== d || dt.getMonth() !== m - 1 || dt.getFullYear() !== y) {
    return "Некорректная дата";
  }
  return "";
};

const checkGuests = (val) => (!/^\d+$/.test(val) ? "Только цифры" : "");

const checkTime = (val) => {
  if (!/^\d{2}:\d{2}$/.test(val)) return "Формат ЧЧ:ММ";
  const [h, m] = val.split(":").map(Number);
  return h > 23 || m > 59 ? "Некорректное время" : "";
};

const checkDuration = (val) =>
  !/^\d+$/.test(val) || +val < 1 || +val > 24 ? "От 1 до 24 часов" : "";

const checkDescription = (val) =>
  val && val.trim().length > 0 ? "" : "Укажите описание";

const getBookingWeekday = (dateStr) => {
  const [d, m, y] = dateStr.split(".").map(Number);
  const bookingDate = new Date(y, m - 1, d);
  return bookingDate.getDay();
};

const isAllowedBookingTime = (dateStr, timeStr) => {
  const [h, min] = timeStr.split(":").map(Number);
  const totalMinutes = h * 60 + min;
  const day = getBookingWeekday(dateStr);

  const isEarlyEnough =
    (day >= 0 && day <= 4 && totalMinutes <= 75) ||
    (day >= 5 && totalMinutes <= 195);

  const isLateEnough = totalMinutes >= 12 * 60;

  return isEarlyEnough || isLateEnough;
};

const SpecialOffer = () => {
  const dispatch = useDispatch();
  const user = useSelector((s) => s.user.data);
  const { loading: spLoading, success: spSuccess } = useSelector(
    (s) => s.reservation.special
  );

  const [bookingName, setBookingName] = useState("");
  const [phone, setPhone] = useState("");
  const [date, setDate] = useState("");
  const [guests, setGuests] = useState("");
  const [time, setTime] = useState("");
  const [defaultTime, setDefaultTime] = useState("");
  const [timeTouched, setTimeTouched] = useState(false);
  const [duration, setDuration] = useState("");
  const [description, setDescription] = useState("");
  const [descTouched, setDescTouched] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (!date && !time) {
      const now = new Date(Date.now() + 15 * 60 * 1000);
      const dd = String(now.getDate()).padStart(2, "0");
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const yyyy = now.getFullYear();
      setDate(`${dd}.${mm}.${yyyy}`);

      const initialTime = `${String(now.getHours()).padStart(2, "0")}:${String(
        now.getMinutes()
      ).padStart(2, "0")}`;

      if (!isAllowedBookingTime(`${dd}.${mm}.${yyyy}`, initialTime)) {
        setDefaultTime("12:00");
      } else {
        setDefaultTime(initialTime);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [errors, setErrors] = useState({
    date: "",
    guests: "",
    time: "",
    duration: "",
    description: "",
  });

  const phoneInitialized = useRef(false);
  const bookingNameInitialized = useRef(false);

  useEffect(() => {
    if (!bookingNameInitialized.current && user?.firstName) {
      setBookingName(user.firstName);
      bookingNameInitialized.current = true;
    }
  }, [user?.firstName]);

  useEffect(() => {
    if (!phoneInitialized.current && user?.phone) {
      const dig = user.phone.replace(/\D/g, "").replace(/^7/, "").slice(0, 10);
      setPhone(dig);
      phoneInitialized.current = true;
    }
  }, [user?.phone]);

  useEffect(() => {
    setErrors({
      date: date ? checkDate(date) : "",
      guests: guests ? checkGuests(guests) : "",
      time: time ? checkTime(time) : "",
      duration: duration ? checkDuration(duration) : "",
      description: checkDescription(description),
    });
  }, [date, guests, time, duration, description]);

  useEffect(() => {
    if (submitAttempted && (bookingName.trim() || phone.length === 10 || date || guests || time || duration || description.trim())) {
      const timer = setTimeout(() => {
        setSubmitAttempted(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [bookingName, phone, date, guests, time, duration, description, submitAttempted]);

  const handleSubmit = () => {
    setSubmitAttempted(true);
    if (!user?.telegramId) return;

    const bookingNameValid = bookingName.trim().length > 0;
    const phoneValid = phone.length === 10;
    const dateValid = date && !checkDate(date);
    const guestsValid = guests && !checkGuests(guests);
    const timeValid = time && !checkTime(time) && isAllowedBookingTime(date, time);
    const durationValid = duration && !checkDuration(duration);
    const descriptionValid = description.trim().length > 0;

    if (!bookingNameValid || !phoneValid || !dateValid || !guestsValid || !timeValid || !durationValid || !descriptionValid) {
      return;
    }

    const formattedPhone = formatPhoneDisplay(phone);
    const offerData = {
      bookingName: bookingName.trim().replace(/[<>&"']/g, ""),
      phone: formattedPhone,
      date,
      guests,
      time,
      duration,
      description: description.trim().replace(/[<>&"']/g, ""),
    };
    dispatch(
      createSpecialOffer({ telegramId: user.telegramId, offerData })
    );
  };

  useEffect(() => {
    if (spSuccess && user?.telegramId) {
      const formattedPhone = formatPhoneDisplay(phone);
      dispatch(
        updateUser({
          ...user,
          phone: formattedPhone,
          lastOrder: {
            phone: formattedPhone,
            date,
            guests,
            time,
            duration,
            description,
          },
        })
      );

      setSubmitAttempted(false);
    }
  }, [spSuccess, dispatch, user?.telegramId]); // eslint-disable-line

  useEffect(() => {
    return () => {
      dispatch(resetSpecialSuccess());
    };
  }, [dispatch]);

  if (!user) return null;

  return (
    <div style={{ marginBottom: "82px" }} className="reservContainer">
      <div className="reservDescription">
        <svg
          className="reservDescriptionIcon"
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            opacity="0.977974"
            d="M3.15599 0C1.41309 0 0 1.4925 0 3.33333V11.6667C0 13.5075 1.41309 15 3.15599 15H11.0459C12.7888 15 14.2019 13.5075 14.2019 11.6667V8.33333C14.2019 7.87333 13.8485 7.5 13.4129 7.5C12.9774 7.5 12.6239 7.87333 12.6239 8.33333V11.6667C12.6239 12.5875 11.9178 13.3333 11.0459 13.3333H3.15599C2.28414 13.3333 1.57799 12.5875 1.57799 11.6667V3.33333C1.57799 2.4125 2.28414 1.66667 3.15599 1.66667H9.46796C9.90348 1.66667 10.257 1.29333 10.257 0.833333C10.257 0.373333 9.90348 0 9.46796 0H3.15599ZM14.2019 0.833333C14 0.833333 13.7885 0.908338 13.6347 1.0675L6.75538 8.20333C6.55261 8.41416 6.34826 8.39917 6.18888 8.15084L5.39989 6.92668C5.15845 6.55084 4.65193 6.44167 4.29057 6.6925C3.92842 6.94334 3.82664 7.4625 4.06807 7.83834L4.85707 9.0625C5.56795 10.17 6.95894 10.3167 7.8655 9.375L14.7692 2.21334C15.0769 1.89417 15.0769 1.3875 14.7692 1.0675C14.6154 0.907505 14.4039 0.833333 14.2019 0.833333Z"
            fill="black"
          />
        </svg>
        <p className="reservDescriptionIconP">Специальное предложение</p>
      </div>

      <div className="reservContent">
        <div className="inputContent" style={{ marginTop: "15px" }}>
          <label className={submitAttempted && !bookingName.trim() ? "error" : ""}>
            {submitAttempted && !bookingName.trim() ? "Укажите имя" : "На какое имя бронь"}
          </label>
          <input
            value={bookingName}
            onChange={(e) => setBookingName(e.target.value)}
            placeholder="Например: Иван"
          />
        </div>

        <div className="inputContent">
          <label className={submitAttempted && phone.length !== 10 ? "error" : ""}>
            {submitAttempted && phone.length !== 10 ? "Укажите номер телефона" : "Номер телефона"}
          </label>
          <input
            value={formatPhoneDisplay(phone)}
            onChange={(e) => {
              const dig = e.target.value
                .replace(/\D/g, "")
                .replace(/^7/, "")
                .slice(0, 10);
              setPhone(dig);
            }}
            placeholder="+7 (___) ___-__-__"
          />
        </div>

        <div className="inputContent">
          <label className={errors.date || (submitAttempted && !date) ? "error" : ""}>
            {errors.date || (submitAttempted && !date ? "Выберите дату" : "Выберите дату")}
          </label>
          <input
            value={date}
            onChange={(e) => setDate(e.target.value)}
            placeholder="06.06.2025"
          />
        </div>

        <div className="inputContent">
          <label className={errors.guests || (submitAttempted && !guests) ? "error" : ""}>
            {errors.guests || (submitAttempted && !guests ? "Укажите количество гостей" : "Количество гостей")}
          </label>
          <input
            value={guests}
            onChange={(e) => setGuests(e.target.value)}
            placeholder="Например: 3"
          />
        </div>

        <div className="inputContent">
          <label
            className={
              (timeTouched || submitAttempted) &&
                (errors.time ||
                  (checkTime(time) === "" &&
                    time &&
                    !isAllowedBookingTime(date, time)) ||
                  (submitAttempted && !time))
                ? "error"
                : ""
            }
          >
            {timeTouched || submitAttempted
              ? errors.time
                ? errors.time
                : checkTime(time) === "" &&
                  time &&
                  !isAllowedBookingTime(date, time)
                  ? getBookingWeekday(date) <= 4
                    ? "Указано не рабочее время"
                    : "Указано не рабочее время"
                  : submitAttempted && !time
                    ? "Укажите время посещения"
                    : "Время посещения"
              : "Время посещения"}
          </label>
          <input
            value={time}
            onChange={(e) => {
              setTime(e.target.value);
              setTimeTouched(false);
            }}
            onBlur={() => setTimeTouched(true)}
            placeholder={`Например: ${defaultTime}`}
          />
        </div>

        <div className="inputContent">
          <label className={errors.duration || (submitAttempted && !duration) ? "error" : ""}>
            {errors.duration || (submitAttempted && !duration ? "Укажите длительность" : "Длительность")}
          </label>
          <input
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            placeholder="Укажите длительность (в часах)"
          />
        </div>

        <div className="inputContent">
          <label className={(descTouched || submitAttempted) && errors.description ? "error" : ""}>
            {(descTouched || submitAttempted) && errors.description ? errors.description : "Дополнительный комментарий"}
          </label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => setDescTouched(true)}
            placeholder="Укажите важные детали события"
          />
        </div>

        <button
          className="reservButton"
          style={{ background: "#366091" }}
          onClick={handleSubmit}
          disabled={spLoading || spSuccess}
        >
          {spLoading ? (
            "Отправка..."
          ) : spSuccess ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ marginRight: 6 }}
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <p>Заявка отправлена</p>
            </div>
          ) : (
            "Отправить"
          )}
        </button>
      </div>
    </div>
  );
};

export default SpecialOffer;
